import { money } from '../utils/format';
import { canViewCosts } from '../utils/permissions';
import ProductDetail from '../components/UI/ProductDetail';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import Modal from '../components/UI/Modal';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import {
  Package, AlertTriangle, DollarSign, FileText, Send, TrendingDown,
  Plus, ClipboardList, ArrowLeftRight, ScanLine, ShoppingCart,
  ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown,
  Truck, Eye, RefreshCw, Clock
} from 'lucide-react';

const fmt = n => Number(n).toFixed(0);
const fmtDollar = n => '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const relTime = ts => {
  const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 7) return d + 'd ago';
  return new Date(ts).toLocaleDateString();
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [loading, setLoading] = useState(true);
  const [countModal, setCountModal] = useState(false);
  const [countItems, setCountItems] = useState([]);
  const [countPLId, setCountPLId] = useState('');
  const [countQty, setCountQty] = useState('');
  const [quickOrderItem, setQuickOrderItem] = useState(null);
  const [viewProductId, setViewProductId] = useState(null);
  const [alertSort, setAlertSort] = useState('severity');
  const [alertDir, setAlertDir] = useState('asc');

  useEffect(() => { api('/api/locations').then(r => setLocations(r.data)).catch(() => {}); }, []);

  const reload = () => {
    setLoading(true);
    const q = locationId ? '?location_id=' + locationId : '';
    api('/api/reports/dashboard' + q).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(reload, [locationId]);

  // ALL useMemo hooks BEFORE any early returns
  const sortedAlerts = useMemo(() => {
    if (!data) return [];
    const copy = [...(data.top_alerts || [])];
    copy.sort((a, b) => {
      let va, vb;
      switch (alertSort) {
        case 'severity': {
          const sev = { out_of_stock: 0, reorder: 1, low: 2, ok: 3 };
          va = sev[a.severity] ?? 3; vb = sev[b.severity] ?? 3;
          return alertDir === 'asc' ? va - vb : vb - va;
        }
        case 'item': va = a.product_name; vb = b.product_name; break;
        case 'location': va = a.location_name; vb = b.location_name; break;
        case 'supplier': va = a.supplier_name || ''; vb = b.supplier_name || ''; break;
        case 'stock': va = Number(a.current_stock); vb = Number(b.current_stock); return alertDir === 'asc' ? va - vb : vb - va;
        default: return 0;
      }
      const cmp = (va || '').localeCompare(vb || '');
      return alertDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [data, alertSort, alertDir]);

  // Helper functions
  const toggleAlertSort = (col) => {
    if (alertSort === col) setAlertDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setAlertSort(col); setAlertDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (alertSort !== col) return <ChevronsUpDown size={11} style={{ opacity: .3, marginLeft: 3 }} />;
    return alertDir === 'asc' ? <ChevronUp size={11} style={{ color: 'var(--gold)', marginLeft: 3 }} /> : <ChevronDown size={11} style={{ color: 'var(--gold)', marginLeft: 3 }} />;
  };

  const openCountModal = async () => {
    const q = locationId ? '?location_id=' + locationId : '';
    const r = await api('/api/inventory' + q);
    setCountItems(r.data || []); setCountPLId(''); setCountQty(''); setCountModal(true);
  };

  const submitCount = async () => {
    if (!countPLId || countQty === '') return;
    await api('/api/inventory/count', { method: 'POST', body: JSON.stringify({ product_location_id: parseInt(countPLId), count_qty: parseFloat(countQty) }) });
    setCountModal(false); reload();
  };

  const quickOrder = (item) => {
    if (!item.default_supplier_id) return alert('No default supplier assigned');
    setQuickOrderItem(item);
  };

  const submitQuickOrder = async (andSend) => {
    const item = quickOrderItem;
    if (!item) return;
    try {
      const r = await api('/api/orders', { method: 'POST', body: JSON.stringify({ location_id: item.location_id, supplier_id: item.default_supplier_id, items: [{ product_id: item.product_id, qty_ordered: Number(item.reorder_qty) || 5, unit_cost: Number(item.unit_cost) || 0 }] }) });
      if (andSend && r.data && r.data.id) {
        await api('/api/orders/' + r.data.id + '/send', { method: 'PUT' });
      }
      setQuickOrderItem(null);
      reload();
    } catch (err) { alert(err.error || 'Failed'); }
  };

  // Early returns AFTER all hooks
  if (loading && !data) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <RefreshCw size={28} style={{ color: 'var(--gold)', animation: 'spin 1s linear infinite' }} />
      {viewProductId && <ProductDetail productId={viewProductId} onClose={() => setViewProductId(null)} />}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (!data) return null;

  const s = data.stats;
  const canEdit = ['admin', 'manager'].includes(user?.role);
  const showCosts = canViewCosts(user);

  const kpis = [
    { label: 'Total Products', value: s.total_products, sub: 'across all locations', icon: Package, color: '#4A9EBF', to: '/products' },
    { label: 'Reorder Alerts', value: s.alert_count, sub: s.out_of_stock + ' out of stock', icon: AlertTriangle, color: '#E74C3C', to: '/inventory?status=reorder', urgent: s.alert_count > 0 },
    { label: 'Low Stock', value: s.low_stock, sub: 'approaching reorder', icon: TrendingDown, color: '#F39C12', to: '/inventory?status=low', urgent: s.low_stock > 0 },
    { label: 'Inventory Value', value: showCosts ? money(s.inventory_value) : '•••', sub: 'total on hand', icon: DollarSign, color: '#2ECC71', to: '/reports' },
    { label: 'Open POs', value: s.open_pos, sub: s.draft_pos + ' draft', icon: FileText, color: '#78A8E8', to: '/orders?status=draft' },
    { label: 'Transfers', value: s.pending_transfers, sub: 'pending approval', icon: ArrowLeftRight, color: '#5BC4C0', to: '/transfers?status=pending', urgent: s.pending_transfers > 0 },
  ];

  const quickActions = [
    { label: 'New PO', icon: Plus, color: '#C9A84C', action: () => nav('/orders?create=true') },
    { label: 'Receive', icon: ClipboardList, color: '#2ECC71', action: () => nav('/orders?status=confirmed') },
    { label: 'Transfer', icon: ArrowLeftRight, color: '#5BC4C0', action: () => nav('/transfers') },
    { label: 'Count', icon: ScanLine, color: '#78A8E8', action: openCountModal },
    { label: 'Add Product', icon: ShoppingCart, color: '#B07FD4', action: () => nav('/products?create=true') },
  ];

  const sevBadge = (sev) => {
    const m = { out_of_stock:{ bg:'rgba(231,76,60,.18)', c:'#E74C3C', t:'OUT' }, reorder:{ bg:'rgba(243,156,18,.18)', c:'#F39C12', t:'REORDER' }, low:{ bg:'rgba(74,158,191,.18)', c:'#4A9EBF', t:'LOW' } };
    const v = m[sev] || m.low;
    return <span style={{ display:'inline-block', padding:'3px 8px', borderRadius:20, fontSize:10, fontWeight:700, letterSpacing:'.5px', background:v.bg, color:v.c }}>{v.t}</span>;
  };

  const activityMeta = (a) => {
    const d = a.ref_detail || '';
    if (a.type === 'order') {
      const sc = { draft:'#4A6A8A', sent:'#4A9EBF', confirmed:'#78A8E8', received:'#2ECC71', cancelled:'#E74C3C' };
      return { icon: FileText, iconColor: '#78A8E8', badge: <span style={{ padding:'2px 8px', borderRadius:12, fontSize:10, fontWeight:600, background:(sc[d]||'#4A6A8A')+'1A', color:sc[d]||'#4A6A8A', textTransform:'uppercase' }}>{d}</span>, onClick:()=>nav('/orders') };
    }
    if (a.type === 'transfer') {
      const sc = { pending:'#F39C12', approved:'#5BC4C0', completed:'#2ECC71', cancelled:'#E74C3C' };
      return { icon: ArrowLeftRight, iconColor: '#5BC4C0', badge: <span style={{ padding:'2px 8px', borderRadius:12, fontSize:10, fontWeight:600, background:(sc[d]||'#4A6A8A')+'1A', color:sc[d]||'#4A6A8A', textTransform:'uppercase' }}>{d}</span>, onClick:()=>nav('/transfers') };
    }
    const v = parseFloat(d); const vc = v < 0 ? '#E74C3C' : v > 0 ? '#2ECC71' : '#4A6A8A';
    return { icon: ScanLine, iconColor: '#78A8E8', badge: v !== 0 && !isNaN(v) ? <span style={{ fontSize:11, fontWeight:600, color:vc }}>{v>0?'+':''}{fmt(v)}</span> : null, onClick:()=>nav('/inventory') };
  };

  const alertTh = (label, col) => (
    <th style={{ ...thS, cursor:'pointer', userSelect:'none' }} onClick={() => toggleAlertSort(col)}>
      <span style={{ display:'inline-flex', alignItems:'center' }}>{label}<SortIcon col={col} /></span>
    </th>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <h1 style={{ fontFamily:'Lora, serif', fontSize:24 }}>Command Center</h1>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <select value={locationId} onChange={e => setLocationId(e.target.value)} style={{ width:200 }}>
            <option value="">All Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <button onClick={reload} className="btn btn-secondary btn-sm" title="Refresh" style={{ padding:'8px 10px' }}>
            <RefreshCw size={15} style={loading?{animation:'spin .6s linear infinite'}:{}} />
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      {canEdit && (
        <div style={{ display:'flex', gap:8, marginBottom:18, overflowX:'auto', paddingBottom:2 }}>
          {quickActions.map((qa,i) => (
            <button key={i} onClick={qa.action} style={{
              display:'flex', alignItems:'center', gap:7, padding:'9px 16px',
              background:'var(--card)', border:'1px solid var(--border)', borderRadius:10,
              color:'var(--white)', fontFamily:'DM Sans,sans-serif', fontSize:13, fontWeight:600,
              cursor:'pointer', whiteSpace:'nowrap', transition:'all .15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor=qa.color; e.currentTarget.style.background=qa.color+'14'; e.currentTarget.style.transform='translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--card)'; e.currentTarget.style.transform='none'; }}
            >
              <qa.icon size={15} style={{ color:qa.color }} />{qa.label}
            </button>
          ))}
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))', gap:12, marginBottom:22 }}>
        {kpis.map((k,i) => (
          <div key={i} onClick={() => nav(k.to)} style={{
            background: k.urgent ? k.color+'0C' : 'var(--card)',
            border:'1px solid '+(k.urgent ? k.color+'40' : 'var(--border)'),
            borderRadius:12, padding:'16px 18px', cursor:'pointer', transition:'all .15s', position:'relative',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,.25)'; e.currentTarget.style.borderColor=k.color+'80'; }}
            onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor=k.urgent?k.color+'40':'var(--border)'; }}
          >
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontSize:11, color:'var(--muted)', fontWeight:500, textTransform:'uppercase', letterSpacing:'.4px', marginBottom:4 }}>{k.label}</div>
                <div style={{ fontFamily:'Lora,serif', fontSize:26, fontWeight:700, color:k.urgent?k.color:'var(--gold)', lineHeight:1.1 }}>{k.value}</div>
                <div style={{ fontSize:11, color:'var(--faint)', marginTop:3 }}>{k.sub}</div>
              </div>
              <k.icon size={20} style={{ color:k.color, opacity:.45, flexShrink:0 }} />
            </div>
            <ChevronRight size={13} style={{ position:'absolute', bottom:8, right:8, color:'var(--faint)', opacity:.35 }} />
          </div>
        ))}
      </div>

      {/* Alerts Table */}
      <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, marginBottom:22, overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <AlertTriangle size={17} style={{ color:'#E74C3C' }} />
            <h3 style={{ fontFamily:'Lora,serif', fontSize:15, margin:0 }}>Reorder Alerts
              {s.alert_count > 0 && <span style={{ marginLeft:8, padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700, background:'rgba(231,76,60,.15)', color:'#E74C3C' }}>{s.alert_count}</span>}
            </h3>
          </div>
          <button onClick={() => nav('/inventory?status=reorder')} className="btn btn-secondary btn-sm" style={{ fontSize:12 }}>View All <ChevronRight size={13} /></button>
        </div>

        {sortedAlerts.length === 0 ? (
          <div style={{ padding:'28px 20px', textAlign:'center', color:'var(--muted)' }}>
            <Package size={28} style={{ opacity:.25, marginBottom:6 }} /><p>All stock levels healthy</p>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--navy)' }}>
                {alertTh('Status','severity')}
                {alertTh('Item','item')}
                {alertTh('Location','location')}
                {alertTh('Stock','stock')}
                <th style={thS}>Min</th>
                {alertTh('Supplier','supplier')}
                <th style={{ ...thS, textAlign:'right' }}>Actions</th>
              </tr></thead>
              <tbody>
                {sortedAlerts.map((a,i) => {
                  const pct = a.reorder_point > 0 ? Number(a.current_stock)/Number(a.reorder_point) : 0;
                  return (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border)', background:i%2===0?'var(--card)':'var(--card2)', transition:'background .12s' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--gold-pale)'}
                      onMouseLeave={e => e.currentTarget.style.background=i%2===0?'var(--card)':'var(--card2)'}
                    >
                      <td style={tdS}>{sevBadge(a.severity)}</td>
                      <td style={tdS}>
                        <span onClick={() => setViewProductId(a.product_id)} style={{ fontWeight:600, fontSize:13, cursor:'pointer', color:'var(--white)', borderBottom:'1px dashed var(--faint)', paddingBottom:1 }}
                          onMouseEnter={e => e.currentTarget.style.color='var(--gold)'}
                          onMouseLeave={e => e.currentTarget.style.color='var(--white)'}
                        >{a.product_name}</span>
                        <div style={{ fontSize:11, color:'var(--faint)' }}>{a.sku}</div>
                      </td>
                      <td style={{ ...tdS, fontSize:13 }}>{a.location_name}</td>
                      <td style={tdS}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <div style={{ width:36, height:4, borderRadius:2, background:'var(--navy)', overflow:'hidden' }}>
                            <div style={{ height:'100%', width:Math.min(pct*100,100)+'%', borderRadius:2, background:pct===0?'#E74C3C':pct<.5?'#F39C12':'#2ECC71' }} />
                          </div>
                          <span style={{ fontWeight:700, fontSize:13, color:Number(a.current_stock)===0?'#E74C3C':'var(--white)', minWidth:24 }}>{fmt(a.current_stock)}</span>
                        </div>
                      </td>
                      <td style={{ ...tdS, color:'var(--faint)', fontSize:13 }}>{fmt(a.reorder_point)}</td>
                      <td style={{ ...tdS, fontSize:12, color:'var(--muted)' }}>{a.supplier_name||'—'}</td>
                      <td style={{ ...tdS, textAlign:'right' }}>
                        <div style={{ display:'flex', gap:3, justifyContent:'flex-end' }}>
                          {canEdit && a.default_supplier_id && <IBtn icon={Truck} color="#C9A84C" title="Quick Order" onClick={() => quickOrder(a)} />}
                          {canEdit && <IBtn icon={ArrowLeftRight} color="#5BC4C0" title="Transfer" onClick={() => nav('/transfers')} />}
                          <IBtn icon={ScanLine} color="#78A8E8" title="Count" onClick={() => { setCountPLId(String(a.id)); setCountQty(String(Number(a.current_stock))); setCountItems([]); setCountModal(true); }} />
                          <IBtn icon={Eye} color="#4A6A8A" title="View" onClick={() => nav('/inventory?search='+encodeURIComponent(a.product_name))} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Chart + Activity */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div className="card" style={{ padding:20 }}>
          <h3 style={{ fontFamily:'Lora,serif', fontSize:15, marginBottom:14, color:'var(--gold)' }}>Inventory Value by Category</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.category_spend} margin={{ bottom:20 }}>
              <XAxis dataKey="name" tick={{ fill:'#B8C8D8', fontSize:10 }} angle={-40} textAnchor="end" height={65} interval={0} />
              <YAxis tick={{ fill:'#B8C8D8', fontSize:10 }} tickFormatter={v => '$'+(v>=1000?(v/1000).toFixed(0)+'k':v)} />
              <Tooltip contentStyle={{ background:'#0A1628', border:'1px solid rgba(201,168,76,0.3)', borderRadius:10, color:'#fff', fontSize:13, padding:'10px 14px', boxShadow:'0 8px 24px rgba(0,0,0,.5)' }} labelStyle={{ color:'#C9A84C', fontWeight:600, marginBottom:4 }} itemStyle={{ color:'#FFFFFF' }} formatter={v => [money(v),'Value']} cursor={{ fill:'rgba(201,168,76,0.06)' }} />
              <Bar dataKey="value" radius={[4,4,0,0]} cursor="pointer"
                onClick={(d) => { if (d && d.name) { api('/api/categories').then(r => { const match = r.data.find(c=>c.name===d.name); if(match) nav('/inventory?category_id='+match.id); else nav('/inventory'); }).catch(()=>nav('/inventory')); } }}
              >
                {(data.category_spend||[]).map((e,i) => <Cell key={i} fill={e.color||'#C9A84C'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card" style={{ padding:0, display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px 12px', borderBottom:'1px solid var(--border)' }}>
            <h3 style={{ fontFamily:'Lora,serif', fontSize:15, margin:0, color:'var(--gold)' }}>Recent Activity</h3>
            <Clock size={14} style={{ color:'var(--faint)' }} />
          </div>
          <div style={{ flex:1, overflowY:'auto', maxHeight:310 }}>
            {data.recent_activity.length === 0 ? (
              <p style={{ padding:24, textAlign:'center', color:'var(--muted)' }}>No recent activity</p>
            ) : data.recent_activity.map((a,i) => {
              const meta = activityMeta(a);
              return (
                <div key={i} onClick={meta.onClick} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 20px', borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background .12s' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--gold-pale)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}
                >
                  <div style={{ width:28, height:28, borderRadius:7, background:meta.iconColor+'14', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <meta.icon size={13} style={{ color:meta.iconColor }} />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{a.description}</div>
                    <div style={{ fontSize:11, color:'var(--faint)', marginTop:1 }}>{relTime(a.timestamp)}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                    {meta.badge}
                    <ChevronRight size={13} style={{ color:'var(--faint)', opacity:.4 }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Quick Order Confirmation */}
      {quickOrderItem && (
        <Modal title="Quick Order" onClose={() => setQuickOrderItem(null)}>
          <div style={{ padding: '16px 18px', background: 'var(--navy)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{quickOrderItem.product_name}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>{quickOrderItem.location_name}</div>
                <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 2 }}>{quickOrderItem.sku} &middot; {quickOrderItem.supplier_name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Qty to order</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gold)', fontFamily: 'Lora,serif' }}>{Number(quickOrderItem.reorder_qty) || 5}</div>
                <div style={{ fontSize: 12, color: 'var(--faint)' }}>{showCosts ? money(quickOrderItem.unit_cost) + ' each' : ''}</div>
              </div>
            </div>
          </div>
          <div style={{ padding: '14px 16px', background: 'rgba(201,168,76,.08)', borderRadius: 10, border: '1px solid rgba(201,168,76,.15)', marginBottom: 20, fontSize: 13, color: 'var(--gold)', lineHeight: 1.5 }}>
            <strong>Send now</strong> will create the PO and immediately email it to <strong>{quickOrderItem.supplier_name}</strong> for confirmation.
            <br /><strong>Save as draft</strong> will create the PO for your review before sending.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setQuickOrderItem(null)}>Cancel</button>
            <button className="btn btn-secondary" onClick={() => submitQuickOrder(false)} style={{ fontWeight: 600 }}>Save as Draft</button>
            <button className="btn btn-primary" onClick={() => submitQuickOrder(true)}><Send size={14} /> Send Now</button>
          </div>
        </Modal>
      )}

      {/* Count Modal */}
      {countModal && (
        <Modal title="Quick Inventory Count" onClose={() => setCountModal(false)}>
          {countItems.length > 0 && !countPLId && (
            <div className="form-group"><label>Select Item</label>
              <select value={countPLId} onChange={e => { setCountPLId(e.target.value); const it = countItems.find(ci=>String(ci.id)===e.target.value); if(it) setCountQty(String(Number(it.current_stock))); }}>
                <option value="">Choose a product...</option>
                {countItems.map(ci => <option key={ci.id} value={ci.id}>{ci.product_name} — {ci.location_name} (stock: {fmt(ci.current_stock)})</option>)}
              </select>
            </div>
          )}
          {countPLId && (
            <div className="form-group"><label>Count Quantity</label>
              <input type="number" value={countQty} onChange={e => setCountQty(e.target.value)} min="0" step="0.1" autoFocus />
            </div>
          )}
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setCountModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitCount} disabled={!countPLId || countQty===''}>Save Count</button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        @media (max-width:900px) {
          [style*="gridTemplateColumns: 1fr 1fr"],
          [style*="gridTemplateColumns:1fr 1fr"] { grid-template-columns:1fr !important; }
        }
        @media (max-width:600px) {
          [style*="repeat(auto-fit"] { grid-template-columns:1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}

const thS = { padding:'9px 14px', fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px', color:'#B8C8D8', textAlign:'left', whiteSpace:'nowrap' };
const tdS = { padding:'9px 14px', fontSize:13, verticalAlign:'middle' };

function IBtn({ icon: Icon, color, title, onClick }) {
  return (
    <button onClick={e => { e.stopPropagation(); onClick(); }} title={title} style={{
      display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28,
      background:color+'12', border:'1px solid '+color+'25', borderRadius:6,
      color, cursor:'pointer', transition:'all .12s', padding:0,
    }}
      onMouseEnter={e => { e.currentTarget.style.background=color+'28'; e.currentTarget.style.borderColor=color; e.currentTarget.style.transform='translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.background=color+'12'; e.currentTarget.style.borderColor=color+'25'; e.currentTarget.style.transform='none'; }}
    >
      <Icon size={13} />
    </button>
  );
}
